import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, UserCheck } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { KbReviewerSelect } from "@/components/KbReviewerSelect";
import { useAuth } from "@/context/AuthContext";

const when = (iso) => (iso ? new Date(iso).toLocaleString([], {
  month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

/** Request a co-worker's review, then approve / request changes. Super admins
 *  can override when the assigned reviewer never responds. */
export function KbReviewPanel({ article, slug, onUpdated, onActivity, reviewer: reviewerProp, onReviewerChange }) {
  const { user } = useAuth();
  const [ownReviewer, setOwnReviewer] = useState(article?.reviewer_id || "");
  const reviewer = reviewerProp !== undefined ? reviewerProp : ownReviewer;
  const setReviewer = onReviewerChange || setOwnReviewer;
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const state = article?.review_state || "none";
  const inReview = state === "in_review";
  const submitterId = article?.pending?.by_id || article?.review_requested_by_id || article?.created_by_id || "";
  const isSubmitter = !!submitterId && submitterId === user?.user_id;
  const isAdmin = user?.role === "super_admin";
  // A super admin may be their own reviewer; everyone else needs a second person.
  const isReviewer = !!article?.reviewer_id && article.reviewer_id === user?.user_id && (!isSubmitter || isAdmin);
  const published = article && !article.is_draft;

  const run = async (fn) => {
    setBusy(true);
    try {
      const { data } = await fn();
      onUpdated?.(data);
      onActivity?.();
      setNote("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const request = () => {
    if (!reviewer) return toast.error("Pick a reviewer first");
    return run(async () => {
      const res = await api.post(`/kb/articles/${slug}/review/request`, { reviewer_id: reviewer, note });
      toast.success(published ? "Submitted — readers keep the published version until it's approved"
        : "Submitted — the reviewer has been notified");
      return res;
    });
  };

  const decide = (decision, publish, override) => run(async () => {
    const res = await api.post(`/kb/articles/${slug}/review/decide`,
      { decision, note, publish, override });
    toast.success(decision === "approved"
      ? (published ? "Approved — the new version is live" : publish ? "Approved & published" : "Approved")
      : "Changes requested — the editor has been notified");
    return res;
  });

  return (
    <div className="space-y-2" data-testid="kb-review-panel">
      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
        <UserCheck className="h-3.5 w-3.5" /> Review
      </div>

      {inReview ? (
        <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-900"
          data-testid="kb-review-status">
          <div>
            In review with <span className="font-semibold">{article.reviewer_name || "—"}</span>
            {article.review_requested_by ? ` · requested by ${article.review_requested_by}` : ""}
            {article.review_requested_at ? ` · ${when(article.review_requested_at)}` : ""}
            {published && " · readers still see the published version"}
          </div>
          {article.review_note && <div className="italic">“{article.review_note}”</div>}
        </div>
      ) : state === "approved" ? (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-2 text-xs text-emerald-800"
          data-testid="kb-review-status">
          Approved by {article.review_decided_by} {when(article.review_decided_at)}
          {article.review_overridden ? " (super admin override — see History)" : ""}
        </div>
      ) : state === "changes_requested" ? (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-2.5 py-2 text-xs text-rose-800"
          data-testid="kb-review-status">
          Changes requested by {article.review_decided_by} {when(article.review_decided_at)}
          {article.review_decision_note ? ` — “${article.review_decision_note}”` : ""}
        </div>
      ) : (
        <p className="text-xs text-slate-400 dark:text-slate-500" data-testid="kb-review-status">Not in review.</p>
      )}

      <div className="space-y-1.5">
        <Label className="text-xs">{inReview ? "Reassign reviewer" : "Reviewer"}</Label>
        <KbReviewerSelect value={reviewer} onChange={setReviewer} testid="kb-review-reviewer" className="h-8 text-xs" />
      </div>

      <textarea data-testid="kb-review-note" value={note} rows={2}
        placeholder="Note for the reviewer / decision note…"
        onChange={(e) => setNote(e.target.value)}
        className="w-full rounded-md border border-slate-200 dark:border-slate-800 px-2.5 py-2 text-xs focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary" />

      <Button size="sm" variant="outline" className="h-7 w-full text-xs" data-testid="kb-review-request"
        onClick={request} disabled={busy || !reviewer}>
        {inReview ? "Re-submit for review" : "Submit for review"}
      </Button>

      {isReviewer && isAdmin && isSubmitter && inReview && (
        <p className="text-[11px] text-slate-400 dark:text-slate-500" data-testid="kb-review-self-note">
          You are reviewing your own article — allowed for super admins and recorded in the article history.
        </p>
      )}
      {inReview && (isReviewer ? (
        <div className="flex gap-1.5">
          <Button size="sm" className="h-7 flex-1 bg-emerald-600 text-xs hover:bg-emerald-700"
            data-testid="kb-review-approve" onClick={() => decide("approved", true, false)} disabled={busy}>
            {published ? "Approve — go live" : "Approve & publish"}
          </Button>
          <Button size="sm" variant="outline" className="h-7 flex-1 border-rose-200 text-xs text-rose-600 hover:bg-rose-50"
            data-testid="kb-review-changes" onClick={() => decide("changes_requested", false, false)} disabled={busy}>
            Request changes
          </Button>
        </div>
      ) : isAdmin ? (
        <div className="space-y-1.5 rounded-md border border-dashed border-slate-300 dark:border-slate-700 px-2.5 py-2">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-500 dark:text-slate-400">
            <ShieldCheck className="h-3.5 w-3.5" /> Super admin
          </div>
          <p className="text-[11px] text-slate-400 dark:text-slate-500">
            {isSubmitter
              ? `You submitted this to ${article.reviewer_name || "a reviewer"}. As a super admin you can publish it yourself — the override is recorded in the notes and the audit log.`
              : `${article.reviewer_name || "The reviewer"} is reviewing this. As a super admin you can decide anyway — the override is recorded in the notes and the audit log.`}
          </p>
          <div className="flex gap-1.5">
            <Button size="sm" className="h-7 flex-1 bg-slate-800 text-xs hover:bg-slate-900"
              data-testid="kb-review-override-approve" onClick={() => decide("approved", true, true)} disabled={busy}>
              {published ? "Publish update now" : "Publish now"}
            </Button>
            <Button size="sm" variant="outline" className="h-7 flex-1 text-xs"
              data-testid="kb-review-override-changes" onClick={() => decide("changes_requested", false, true)} disabled={busy}>
              Request changes
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-[11px] text-slate-400 dark:text-slate-500" data-testid="kb-review-waiting">
          {isSubmitter
            ? `You submitted this version — ${article.reviewer_name || "someone else"} has to approve it.`
            : `Waiting on ${article.reviewer_name} to decide.`}
        </p>
      ))}
    </div>
  );
}
